<script setup lang="ts">
import type { Component } from 'vue';
import type { DButton } from '#components';
import type { OnboardingPopupTaskRow } from '~/constants/onboarding-popup-tasks';
import { unrefElement, useDevicePixelRatio } from '@vueuse/core';
import { computed, ref, useTemplateRef, watch } from 'vue';
import ConfiguredPermissionsIllustration from '~/assets/img/illustrations/onboarding/configured-permissions.svg';
import ConnectedAiAssistantIllustration from '~/assets/img/illustrations/onboarding/connected-ai-assistant.svg';
import ConnectedDataSourceIllustration from '~/assets/img/illustrations/onboarding/connected-data-source.svg';
import ConnectedToStudioIllustration from '~/assets/img/illustrations/onboarding/connected-to-studio.svg';
import CreatedWorkspaceIllustration from '~/assets/img/illustrations/onboarding/created-workspace.svg';
import InvitedTeamIllustration from '~/assets/img/illustrations/onboarding/invited-team.svg';
import ManagedSchemaIllustration from '~/assets/img/illustrations/onboarding/managed-schema.svg';
import { ONBOARDING_TRANSITION_DURATION } from './constants';

const {
	row,
	workspaceRequiredLabel,
} = defineProps<{
	row: OnboardingPopupTaskRow;
	workspaceRequiredLabel: string;
}>();

const emit = defineEmits<{
	action: [];
}>();

const ACTION_WIDTH_TRANSITION_DURATION = 75;
const { pixelRatio } = useDevicePixelRatio();
const animateBodyHeight = ref(false);

watch(() => row.id, () => animateBodyHeight.value = true, { once: true });

const actionRef = useTemplateRef<InstanceType<typeof DButton>>('action');
const actionElement = computed(() => unrefElement(actionRef));

defineExpose({ actionElement });

const taskIllustrations: Record<OnboardingPopupTaskRow['id'], Component | string> = {
	createdWorkspace: CreatedWorkspaceIllustration,
	connectedDataSource: ConnectedDataSourceIllustration,
	managedSchema: ManagedSchemaIllustration,
	configuredPermissions: ConfiguredPermissionsIllustration,
	connectedToStudio: ConnectedToStudioIllustration,
	connectedAiAssistant: ConnectedAiAssistantIllustration,
	invitedTeam: InvitedTeamIllustration,
};

const contentTransition = {
	enterActiveClass: 'motion-safe:transition-[opacity,filter] motion-safe:duration-150 ease-out',
	enterFromClass: 'motion-safe:opacity-0 motion-safe:blur-[2px]',
	enterToClass: 'opacity-100 blur-none',
	leaveActiveClass: 'motion-safe:transition-[opacity,filter] motion-safe:duration-100 ease-out',
	leaveFromClass: 'opacity-100 blur-none',
	leaveToClass: 'motion-safe:opacity-0 motion-safe:blur-[2px]',
};

const footerContentKey = computed(() =>
	row.disabled ? `locked:${workspaceRequiredLabel}` : 'action',
);
</script>

<template>
	<article data-test="onboarding-detail" :data-detail-id="row.id" :style="{ '--onboarding-pixel-ratio': pixelRatio }">
		<div class="px-5 pt-5">
			<div
				class="
					grid aspect-20/13 overflow-hidden rounded-2xl border border-default bg-muted text-highlighted
					dark:[--onboarding-art-accent-edge:color-mix(in_srgb,var(--ui-primary)_40%,var(--color-neutral-900))]
					dark:[--onboarding-art-accent-outline:color-mix(in_srgb,var(--ui-primary)_65%,var(--ui-text-highlighted))]
					dark:[--onboarding-art-accent-surface:color-mix(in_srgb,var(--ui-primary)_30%,var(--color-neutral-700))]
					dark:[--onboarding-art-edge:var(--color-neutral-900)] dark:[--onboarding-art-outline:var(--color-neutral-400)]
					dark:[--onboarding-art-surface:var(--color-neutral-800)]
				"
			>
				<Transition v-bind="contentTransition">
					<component
						:is="taskIllustrations[row.id]"
						:key="row.id"
						data-test="onboarding-illustration"
						aria-hidden="true"
						class="col-start-1 row-start-1 size-full"
					/>
				</Transition>
			</div>
		</div>

		<!-- Match Floating UI's device-pixel positioning so the bottom-aligned footer stays still. -->
		<DAnimatedSheet
			dimension="height"
			:duration="animateBodyHeight ? ONBOARDING_TRANSITION_DURATION : 0"
			class="grid w-full"
			:ui="{
				root: `
					h-[round(nearest,var(--onboarding-body-height),calc(1px/var(--onboarding-pixel-ratio)))]
					[--onboarding-body-height:var(--sheet-height)]
					motion-safe:[transition-property:--onboarding-body-height]
				`,
			}"
		>
			<Transition v-bind="contentTransition">
				<div
					:key="row.id"
					data-test="onboarding-body"
					class="col-start-1 row-start-1 px-5 pt-3 pb-5"
				>
					<h3 class="text-sm font-semibold text-highlighted">
						{{ row.label }}
					</h3>
					<p class="mt-1.5 text-xs/5 text-toned">
						{{ row.description }}
					</p>
				</div>
			</Transition>
		</DAnimatedSheet>

		<footer class="grid h-15 items-center border-t border-default px-5">
			<Transition v-bind="contentTransition">
				<div :key="footerContentKey" class="col-start-1 row-start-1 flex items-center">
					<div
						v-if="row.disabled"
						data-test="onboarding-locked"
						:data-locked-id="row.id"
						class="flex items-center gap-2 text-xs/5 text-toned"
					>
						<DIcon data-test="onboarding-locked-icon" name="lucide:lock-keyhole" class="size-4 shrink-0" />
						<span>{{ workspaceRequiredLabel }}</span>
					</div>
					<DButton
						v-else
						ref="action"
						data-test="onboarding-action"
						:data-action-id="row.id"
						trailing-icon="lucide:arrow-right"
						color="neutral"
						variant="solid"
						size="sm"
						@click="emit('action')"
					>
						<DAnimatedSheet
							data-test="onboarding-action-width"
							dimension="width"
							:duration="ACTION_WIDTH_TRANSITION_DURATION"
							:ui="{
								root: 'inline-flex items-center motion-safe:ease-in-out',
								sheet: 'inline-flex shrink-0 items-center',
							}"
						>
							<span data-test="onboarding-action-label">{{ row.actionLabel }}</span>
						</DAnimatedSheet>
					</DButton>
				</div>
			</Transition>
		</footer>
	</article>
</template>

<style scoped>
@property --onboarding-body-height {
	syntax: '<length>';
	inherits: false;
	initial-value: 0px;
}
</style>
